import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './AdminModals.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, getApiBaseUrl } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

export const UserFormPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const { users, companies, projects, refreshData } = useAdmin();

  const user = userId ? users.find((u: any) => (u._id || u.id) === userId) : null;
  const isNew = !userId;

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'viewer',
    company: '',
    isActive: true,
    projects: [] as string[],
  });

  useEffect(() => {
    if (user) {
      const names = user.name?.split(' ') || [];
      setFormData({
        firstName: (user as any).firstName || names[0] || '',
        lastName: (user as any).lastName || names.slice(1).join(' ') || '',
        email: user.email,
        password: '',
        role: user.role || 'viewer',
        company: (user as any).company?._id || (user as any).company || '',
        isActive: (user as any).isActive !== false,
        projects: ((user as any).projects || []).map((p: any) => p._id || p),
      });
      const av = (user as any).avatar;
      if (av) {
        const origin = getApiBaseUrl().replace(/\/api$/, '');
        setAvatarPreview(av.startsWith('http') ? av : `${origin}${av}`);
      }
    }
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const uid = user?.id || (user as any)?._id;
    if (!file || !uid) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bestand te groot', 'Maximaal 5 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const avatarUrl = await usersAPI.uploadAvatar(uid, file);
      const origin = getApiBaseUrl().replace(/\/api$/, '');
      setAvatarPreview(avatarUrl.startsWith('http') ? avatarUrl : `${origin}${avatarUrl}`);
      toast.success('Profielfoto bijgewerkt');
    } catch (err: any) {
      toast.error('Upload mislukt', err.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleProjectToggle = (projectId: string) => {
    setFormData(prev => ({
      ...prev,
      projects: prev.projects.includes(projectId)
        ? prev.projects.filter(id => id !== projectId)
        : [...prev.projects, projectId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew && !formData.password) {
      toast.error('Wachtwoord is verplicht', 'Vul een wachtwoord in voor de nieuwe gebruiker.');
      return;
    }

    setSaving(true);
    try {
      const { projects: selectedProjects, ...userFields } = formData;
      const submitData: any = { ...userFields };
      if (!isNew && !submitData.password) {
        delete submitData.password;
      }

      if (user) {
        const uid = user.id || (user as any)._id;
        await usersAPI.update(uid, submitData);
        if (selectedProjects) {
          await usersAPI.assignProjects(uid, selectedProjects);
        }
        toast.success('Gebruiker bijgewerkt');
      } else {
        const newUser = await usersAPI.create(submitData);
        if (selectedProjects?.length > 0 && newUser?._id) {
          await usersAPI.assignProjects(newUser._id, selectedProjects);
        }
        toast.success('Gebruiker aangemaakt');
      }
      refreshData();
      navigate('/admin/users');
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
          <div>
            <button
              onClick={() => navigate('/admin/users')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#666D70', fontSize: '0.8125rem', padding: 0, marginBottom: '0.5rem',
                display: 'flex', alignItems: 'center', gap: '0.25rem',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Terug naar gebruikers
            </button>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#323232' }}>
              {isNew ? 'Nieuwe gebruiker' : `${formData.firstName} ${formData.lastName}`.trim() || 'Gebruiker bewerken'}
            </h1>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outline" onClick={() => navigate('/admin/users')}>
            Annuleren
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Opslaan...' : isNew ? 'Aanmaken' : 'Opslaan'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Avatar + Basic Info */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#323232', paddingBottom: '0.75rem', borderBottom: '1px solid #F0F0F0' }}>Profielfoto</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', background: '#1E3A2F',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.5rem', fontWeight: 600, overflow: 'hidden', flexShrink: 0,
                }}>
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    (formData.firstName?.[0] || '') + (formData.lastName?.[0] || '') || 'U'
                  )}
                </div>
                {!isNew && (
                  <div>
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      style={{
                        fontSize: '0.8125rem', fontWeight: 600, padding: '0.5rem 1rem',
                        border: '1px solid #DDD', borderRadius: 8, background: '#fff',
                        cursor: 'pointer', color: '#323232',
                      }}
                    >
                      {uploadingAvatar ? 'Uploaden...' : 'Foto wijzigen'}
                    </button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleAvatarUpload}
                      style={{ display: 'none' }}
                    />
                    <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: '#9DA6A8' }}>
                      JPG, PNG of WebP. Max 5 MB.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Personal Info */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#323232', paddingBottom: '0.75rem', borderBottom: '1px solid #F0F0F0' }}>Persoonsgegevens</h3>
              <div className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="firstName">Voornaam *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="lastName">Achternaam *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email">E-mail *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                {isNew && (
                  <div className={styles.formGroup}>
                    <label htmlFor="password">Wachtwoord *</label>
                    <input
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                    <small>Minimaal 6 tekens</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Role & Company */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#323232', paddingBottom: '0.75rem', borderBottom: '1px solid #F0F0F0' }}>Rol & Organisatie</h3>
              <div className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="role">Rol *</label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="viewer">Lezer</option>
                      <option value="stakeholder">Stakeholder</option>
                      <option value="projectManager">Projectmanager</option>
                      <option value="admin">Admin</option>
                      {isSuperAdmin && <option value="superAdmin">Super Admin</option>}
                    </select>
                  </div>

                  {isSuperAdmin && (
                    <div className={styles.formGroup}>
                      <label htmlFor="company">Organisatie</label>
                      <select
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      >
                        <option value="">Selecteer organisatie</option>
                        {companies.map((company: any) => (
                          <option key={company._id} value={company._id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ accentColor: '#1E3A2F' }}
                    />
                    <span>Actieve gebruiker</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Projects */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#323232', paddingBottom: '0.75rem', borderBottom: '1px solid #F0F0F0' }}>Projecten toewijzen</h3>
              {projects.length > 0 ? (
                <div className={styles.form}>
                  <div style={{
                    maxHeight: '300px', overflowY: 'auto', border: '1px solid #DDDDDD',
                    borderRadius: 8, padding: '0.5rem',
                  }}>
                    {projects.map((project: any) => {
                      const projectId = project._id || project.id;
                      const location = typeof project.location === 'object'
                        ? project.location?.city || ''
                        : project.location || '';
                      return (
                        <label
                          key={projectId}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.5rem 0.375rem', cursor: 'pointer',
                            borderRadius: 6,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.projects.includes(projectId)}
                            onChange={() => handleProjectToggle(projectId)}
                            style={{ accentColor: '#1E3A2F' }}
                          />
                          <span style={{ flex: 1 }}>
                            <strong>{project.name}</strong>
                            {location && <span style={{ color: '#9DA6A8', marginLeft: '0.5rem', fontSize: '0.85em' }}>({location})</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <small style={{ color: '#9DA6A8', marginTop: '0.5rem', display: 'block' }}>
                    {formData.projects.length} project(en) geselecteerd
                  </small>
                </div>
              ) : (
                <p style={{ color: '#9DA6A8', fontSize: '0.875rem' }}>Geen projecten beschikbaar</p>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
